import { useEffect, useState } from "react";
import { toast } from "sonner";
import { videoService } from "../api/video.api";

interface AllowGuestsToggleProps {
  roomId: string;
  initial: boolean;
  /** Notify parent when the server-side state has flipped (for cache sync). */
  onChange?: (next: boolean) => void;
}

/**
 * Owner-only switch that flips Room.allow_guests via PATCH /rooms/<id>/.
 * Optimistic UI — toggle moves immediately, reverts on failure with a toast.
 * Parent decides whether to render this (e.g. only when room.is_administrable).
 *
 * Matches the design's own pill-switch (weco2/call.jsx's `linkJoinOn`
 * toggle) — not a native checkbox. The thumb's base position is an
 * explicit `start-0.5` (not left implicit/auto): the track `<button>`
 * renders with `display:block` + the browser's default button
 * `text-align:center`, which pulls an unanchored absolutely-positioned
 * child to a static position that is NOT the start edge — `translate-x`
 * alone (mirroring the design mock's own CSS, which never sets an
 * explicit anchor either) measures from that wrong starting point, so
 * the thumb renders overlapping the track's end edge instead of sliding
 * between the two ends. Same `start-0.5` + `ltr:translate-x-4
 * rtl:-translate-x-4` convention as the existing autoreply toggle in
 * `modules/email/settings/internal/AutoreplySettings.tsx`.
 */
export function AllowGuestsToggle({ roomId, initial, onChange }: AllowGuestsToggleProps) {
  const [enabled, setEnabled] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(initial);
  }, [initial]);

  const handleChange = async () => {
    if (busy) return;
    const next = !enabled;
    setEnabled(next);
    setBusy(true);
    try {
      await videoService.updateRoom(roomId, { allow_guests: next });
      onChange?.(next);
    } catch (err) {
      console.error("AllowGuestsToggle updateRoom failed:", err);
      setEnabled(!next);
      toast.error("Could not update guest access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button
        type="button"
        onClick={() => void handleChange()}
        disabled={busy}
        aria-pressed={enabled}
        aria-label="Anyone with the link can join"
        className={`mt-0.5 w-9 h-5 rounded-full shrink-0 relative transition-colors ${enabled ? "bg-primary" : "bg-muted"}`}
      >
        <span
          className={`absolute start-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? "ltr:translate-x-4 rtl:-translate-x-4" : ""}`}
        />
      </button>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold text-foreground">Anyone with the link can join</span>
        <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">
          Externals (Gmail etc.) can join as guests. Toggle off to restrict to WeCo accounts.
        </span>
      </span>
    </label>
  );
}
